/**
 * Schedule page data layer: loads jobs, groups, and blocks; derives visible projects,
 * flat rows, and ScopeBars; owns zoom/scroll UI state; runs create / PATCH / optimistic
 * updates for blocks (resize, drag-drop, new scope) and groups.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { usePlanningContext } from "../../../../context/domain/planning/PlanningContext";
import {
  getPlanningDayLocks,
  getPlanningJobs,
  setPlanningDayLocked,
} from "../../../../services/api/planning";
import {
  fetchAllScheduleBlocksInRange,
  loadScheduleWeatherSplitByDay,
  getScheduleGroups,
  scheduleListResults,
} from "../../../../services/api/schedule";
import {
  getWeekStart,
  toLocalDateKey,
  today,
} from "../../../../utils/datetime/core";
import {
  mapAspectFromApi,
  mapBlockFromApi,
  mapJobNumberToProject,
  mapScheduleGroupFromApi,
} from "../model/scheduleApiMappers";
import { buildTimelineBars } from "../model/scheduleBarModel";
import {
  blockDateSpanHasLockedDay,
  getAspectBlockId,
  mergeBlocksContainsLocked,
} from "../model/scheduleBlockSelectors";
import { sameScheduleBlockId } from "../model/scheduleIds";
import {
  mergeAspectDaysAfterBlockSpanChange,
} from "../model/scheduleMaterializeAspectDays";
import { buildScheduleOverlapLayout } from "../model/scheduleOverlapLayout";
import {
  SCHEDULE_DEFAULT_CELL_WIDTH_PX,
  SCHEDULE_DISPATCH_GROUP_NAME,
  SCHEDULE_ROW_HEIGHT,
} from "../constants";
import {
  getEffectiveFilterIds,
  mergeSelectedFilterOptions,
} from "../../shared/filters/planningFilterModel";
import {
  blockMatchesWorkScopeFilter,
  blockMatchesWorkerFilter,
  collectProjectFilterOptions,
  collectWorkScopeFilterOptions,
  collectWorkerFilterOptions,
  filterScheduleBlocksForSearch,
  filterScheduleBlocksForToolbar,
  filterScheduleRowsForSearch,
} from "../model/scheduleFilterModel";
import { flattenVisibleScheduleRows } from "../model/scheduleRowModel";
import {
  buildScheduleDataWindowAroundDate,
  extendLoadedWindowWithChunks,
  getScheduleWindowPrefetchChunks,
  mergeScheduleAspectRows,
  mergeScheduleBlocksById,
  scheduleChunkKey,
} from "../model/scheduleDataWindow";
import {
  clampLocalDateToJobRange,
  jobOverlapsVisibleRange,
} from "../model/scheduleJobDateRange";
import { useScheduleGroupActions } from "./hooks/useScheduleGroupActions";
import { useScheduleDeleteScopeBlock } from "./hooks/useScheduleDeleteScopeBlock";
import { useScheduleBlockActions } from "./hooks/useScheduleBlockActions";
import { useScheduleCrewActions } from "./hooks/useScheduleCrewActions";
import { useScheduleCreatePasteActions } from "./hooks/useScheduleCreatePasteActions";
import { usePlanningSyncPoller } from "../../sync/usePlanningSyncPoller";

/**
 * Schedule page data layer: loads jobs/groups/blocks, derives rows and bars, owns timeline
 * UI state, and composes create/PATCH/optimistic flows via domain hooks.
 *
 * @param {Object} [options] Schedule controller options.
 * @param {boolean} [options.compact=false] Whether to render the compact schedule chrome.
 * @param {boolean} [options.readOnly=false] Whether schedule mutation actions are disabled in the UI.
 * @param {Object|null} [options.singleProject=null] Optional project row used to scope schedule data.
 * @param {Object} [options.defaultFilters] Optional mount-time filter seeds (soft defaults).
 * @param {Array<string|number>} [options.defaultFilters.projectIds] Initial project filter ids.
 * @param {Array<string|number>} [options.defaultFilters.workScopeIds] Initial work-scope filter ids.
 * @param {Array<string|number>} [options.defaultFilters.workerIds] Initial worker filter ids.
 * @param {Date|null} [options.initialTimelineStart=null] Optional timeline week-start seed.
 * @param {boolean} [options.loadDayDetails=true] Whether to load DayDetails/locks.
 * @param {boolean} [options.loadWeather=true] Whether to load weather rows.
 * @returns {import('./scheduleContextValue').ScheduleContextValue}
 */
export function useScheduleController(options = {}) {
  const {
    compact = false,
    readOnly = false,
    singleProject = null,
    defaultFilters = null,
    initialTimelineStart = null,
    loadDayDetails = !compact,
    loadWeather = !compact,
  } = options;
  const singleProjectId = singleProject?.id == null ? null : String(singleProject.id);
  const hasSingleProject = Boolean(singleProjectId);
  /** Normalize optional mount-time filter ids to stable string arrays. */
  const initialFilterProjectIds = (defaultFilters?.projectIds ?? []).map(String);
  const initialFilterWorkScopeIds = (defaultFilters?.workScopeIds ?? []).map(String);
  const initialFilterWorkerIds = (defaultFilters?.workerIds ?? []).map(String);
  const {
    scopeObjects,
    workers,
    notify: enqueueNotification,
    runPlanningOperation,
    clientInstanceId,
    setPlanningLastUpdatedFromEvent,
    planningLastUpdated,
  } = usePlanningContext();

  /** Normalize scope catalog input from PlanningContext to a stable array. */
  const workScopes = useMemo(() => {
    if (!scopeObjects?.length) {
      return [];
    }
    return scopeObjects;
  }, [scopeObjects]);

  const [allJobs, setAllJobs] = useState([]);
  const allJobsRef = useRef([]);
  const [groups, setGroups] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [workScopeAspects, setWorkScopeAspects] = useState([]);
  const [isScheduleDataLoading, setIsScheduleDataLoading] = useState(true);
  const [isScheduleWindowLoading, setIsScheduleWindowLoading] = useState(false);
  const [loadedWindow, setLoadedWindow] = useState(null);
  const blockDraftWorkers = useRef([]);  // Draft crew for block creation
  const blocksRef = useRef(blocks);
  const workScopeAspectsRef = useRef(workScopeAspects);
  const loadedWindowRef = useRef(loadedWindow);
  const timelineStartRef = useRef(null);
  const timelineLayoutRef = useRef(null);
  const prefetchInFlightKeysRef = useRef(new Set());
  /** Chunks already merged into state — avoids repeat fetches on layout-only updates. */
  const fetchedChunkKeysRef = useRef(new Set());
  const lastPrefetchTriggerKeyRef = useRef(null);
  const lastDayDetailsRangeKeyRef = useRef(null);
  blocksRef.current = blocks;
  workScopeAspectsRef.current = workScopeAspects;
  loadedWindowRef.current = loadedWindow;

  /** Invalidates in-flight day-details fetches when the visible timeline range changes. */
  const scheduleDayDetailsFetchGenRef = useRef(0);
  /** Invalidates in-flight weather fetches when the visible timeline range changes. */
  const scheduleWeatherFetchGenRef = useRef(0);
  const [zoomLevel, setZoomLevel] = useState(2);
  const [showWeekends, setShowWeekends] = useState(false);
  const [hideEmptyProjects, setHideEmptyProjects] = useState(true);
  const [projectSearchQuery, setProjectSearchQuery] = useState("");
  const [scheduleViewMode, setScheduleViewMode] = useState("scopes");
  const [filterProjectId, setFilterProjectId] = useState(initialFilterProjectIds);
  const [filterWorkScopeId, setFilterWorkScopeId] = useState(
    initialFilterWorkScopeIds
  );
  const [filterWorkerId, setFilterWorkerId] = useState(initialFilterWorkerIds);
  const [timelineStart, setTimelineStart] = useState(() =>
    getWeekStart(initialTimelineStart ?? today())
  );
  const [timelineLayout, setTimelineLayout] = useState({
    cellWidth: SCHEDULE_DEFAULT_CELL_WIDTH_PX,
    timelineWidth: 0,
    visibleDays: [],
  });
  timelineStartRef.current = timelineStart;
  timelineLayoutRef.current = timelineLayout;
  const [horizontalScrollLeft, setHorizontalScrollLeft] = useState(0);
  /** Dispatch DayDetails rows keyed by `YYYY-MM-DD` for visible timeline days (read-only). */
  const [scheduleDayDetailsByDate, setScheduleDayDetailsByDate] = useState({});
  /** Weather rows keyed by `YYYY-MM-DD` for visible timeline days (read-only). */
  const [scheduleWeatherByDate, setScheduleWeatherByDate] = useState({});
  const [dragPreviewBlock, setDragPreviewBlock] = useState(null);

  const [expandedProjectIds, setExpandedProjectIds] = useState(() => new Set());
  const [expandedGroupIds, setExpandedGroupIds] = useState(() => new Set());

  const initialProjectExpandDone = useRef(false);
  const initialGroupExpandDone = useRef(false);

  /**
   * Map API block rows to client blocks and flat aspect rows.
   *
   * @param {Array<Object>} bList Raw block list from the schedule API.
   * @returns {{ blocks: Array<Object>, aspects: Array<Object> }}
   */
  const mapBlocksAndAspectsFromApi = useCallback((bList) => {
    const mappedBlocks = bList.map(mapBlockFromApi);
    const aspects = [];
    for (const b of bList) {
      const bid = b.id;
      for (const a of b.aspects ?? []) {
        const row = mapAspectFromApi(a, bid);
        if (row) {
          aspects.push(row);
        }
      }
    }
    return { blocks: mappedBlocks, aspects };
  }, []);

  /**
   * Apply a blocks API payload to React state (replace or merge by block id).
   *
   * @param {Array<Object>} bList Raw block list from the schedule API.
   * @param {{ replace?: boolean, aspectWindow?: Object|null }} [options]
   */
  const applyBlocksApiPayload = useCallback(
    (bList, { replace = false, aspectWindow = null } = {}) => {
      const { blocks: mappedBlocks, aspects } = mapBlocksAndAspectsFromApi(bList);
      if (replace) {
        setBlocks(mappedBlocks);
        setWorkScopeAspects(aspects);
        return;
      }
      const touchedBlockIds = mappedBlocks.map((block) => block.id);
      setBlocks((prev) => mergeScheduleBlocksById(prev, mappedBlocks));
      setWorkScopeAspects((prev) =>
        mergeScheduleAspectRows(prev, aspects, touchedBlockIds, aspectWindow)
      );
    },
    [mapBlocksAndAspectsFromApi]
  );

  /** Reload schedule groups and replace the local group tree state. */
  const reloadScheduleGroups = useCallback(async () => {
    const groupsRaw = await getScheduleGroups();
    const gList = scheduleListResults(groupsRaw);
    setGroups(gList.map(mapScheduleGroupFromApi).filter(Boolean));
  }, []);

  /**
   * Reload schedule blocks and their nested WorkScopeAspects.
   *
   * Schedule reads WorkScopeAspects through the block list response, so aspect
   * sync events reload blocks and rebuild the flat aspect rows together.
   */
  const reloadScheduleBlocks = useCallback(async () => {
    const window = loadedWindowRef.current;
    if (!window) {
      return;
    }
    const bList = await fetchAllScheduleBlocksInRange(
      window.startKey,
      window.endKey
    );
    applyBlocksApiPayload(bList, { replace: true });
  }, [applyBlocksApiPayload]);

  /** Reload all Schedule page data needed to rebuild rows and bars. */
  const reloadScheduleData = useCallback(
    async ({ setLoading = true, centerDate = null } = {}) => {
      if (setLoading) {
        setIsScheduleDataLoading(true);
      }
      const anchor = centerDate ?? timelineStartRef.current;
      fetchedChunkKeysRef.current.clear();
      lastPrefetchTriggerKeyRef.current = null;
      try {
        const window = buildScheduleDataWindowAroundDate(anchor);
        const [jobsRaw, groupsRaw, bList] = await Promise.all([
          getPlanningJobs(window.startKey, window.endKey),
          getScheduleGroups(),
          fetchAllScheduleBlocksInRange(window.startKey, window.endKey),
        ]);
        const jobList = Array.isArray(jobsRaw) ? jobsRaw : [];
        setAllJobs(jobList);
        allJobsRef.current = jobList;
        const gList = scheduleListResults(groupsRaw);
        setGroups(gList.map(mapScheduleGroupFromApi).filter(Boolean));
        applyBlocksApiPayload(bList, { replace: true });
        setLoadedWindow(window);
        fetchedChunkKeysRef.current.add(scheduleChunkKey(window));
      } catch (e) {
        console.error("Schedule load failed", e);
        setAllJobs([]);
        allJobsRef.current = [];
        setGroups([]);
        setBlocks([]);
        setWorkScopeAspects([]);
        setLoadedWindow(null);
      } finally {
        if (setLoading) {
          setIsScheduleDataLoading(false);
        }
      }
    },
    [applyBlocksApiPayload]
  );

  /**
   * Fetch additional block chunks when the visible timeline nears the loaded window edge.
   */
  const ensureScheduleWindowCovers = useCallback(async () => {
    const window = loadedWindowRef.current;
    const vd = timelineLayoutRef.current?.visibleDays;
    if (!window || !vd?.length || isScheduleDataLoading) {
      return;
    }
    const visibleStartKey = toLocalDateKey(vd[0]);
    const visibleEndKey = toLocalDateKey(vd[vd.length - 1]);
    const chunks = getScheduleWindowPrefetchChunks(
      window,
      visibleStartKey,
      visibleEndKey
    );
    if (!chunks.length) {
      return;
    }

    const pendingChunks = chunks.filter((chunk) => {
      const chunkKey = scheduleChunkKey(chunk);
      return (
        !fetchedChunkKeysRef.current.has(chunkKey)
        && !prefetchInFlightKeysRef.current.has(chunkKey)
      );
    });
    if (!pendingChunks.length) {
      return;
    }

    setIsScheduleWindowLoading(true);
    try {
      for (const chunk of pendingChunks) {
        const chunkKey = scheduleChunkKey(chunk);
        prefetchInFlightKeysRef.current.add(chunkKey);
        try {
          const [bList, jobsRaw] = await Promise.all([
            fetchAllScheduleBlocksInRange(chunk.startKey, chunk.endKey),
            getPlanningJobs(chunk.startKey, chunk.endKey),
          ]);
          applyBlocksApiPayload(bList, {
            replace: false,
            aspectWindow: chunk,
          });
          const incomingJobs = Array.isArray(jobsRaw) ? jobsRaw : [];
          if (incomingJobs.length) {
            const byId = new Map(
              (allJobsRef.current ?? []).map((job) => [job.id, job])
            );
            for (const job of incomingJobs) {
              byId.set(job.id, job);
            }
            const mergedJobs = Array.from(byId.values());
            allJobsRef.current = mergedJobs;
            setAllJobs(mergedJobs);
          }
          fetchedChunkKeysRef.current.add(chunkKey);
        } finally {
          prefetchInFlightKeysRef.current.delete(chunkKey);
        }
      }
      setLoadedWindow((prev) =>
        extendLoadedWindowWithChunks(prev ?? window, pendingChunks)
      );
    } catch (e) {
      console.error("Schedule window prefetch failed", e);
    } finally {
      setIsScheduleWindowLoading(false);
    }
  }, [applyBlocksApiPayload, isScheduleDataLoading]);

  /** Initial schedule bootstrap (mount only — not on timeline pan/zoom). */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await reloadScheduleData({
          centerDate: timelineStartRef.current,
        });
        if (cancelled) {
          return;
        }
      } catch (e) {
        if (!cancelled) {
          console.error("Schedule initial load failed", e);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-time page bootstrap
  }, []);

  /** Prefetch block chunks only when the visible calendar range actually moves. */
  useEffect(() => {
    if (isScheduleDataLoading || !loadedWindow) {
      return;
    }
    const vd = timelineLayout.visibleDays;
    if (!vd?.length) {
      return;
    }
    const visibleStartKey = toLocalDateKey(vd[0]);
    const visibleEndKey = toLocalDateKey(vd[vd.length - 1]);
    const triggerKey = `${visibleStartKey}|${visibleEndKey}|${toLocalDateKey(timelineStart)}`;
    if (triggerKey === lastPrefetchTriggerKeyRef.current) {
      return;
    }
    lastPrefetchTriggerKeyRef.current = triggerKey;
    void ensureScheduleWindowCovers();
  }, [
    timelineStart,
    timelineLayout.visibleDays,
    loadedWindow,
    isScheduleDataLoading,
    ensureScheduleWindowCovers,
  ]);

  /** Loads DayDetails for the visible range and keeps Schedule day metadata in sync (stale-response safe). */
  const reloadScheduleDayDetailsForRange = useCallback(async (startIso, endIso) => {
    scheduleDayDetailsFetchGenRef.current += 1;
    const gen = scheduleDayDetailsFetchGenRef.current;
    try {
      const rows = await getPlanningDayLocks(startIso, endIso);
      if (gen !== scheduleDayDetailsFetchGenRef.current) {
        return;
      }
      const next = {};
      if (Array.isArray(rows)) {
        for (const row of rows) {
          if (row?.date != null) {
            next[row.date] = row;
          }
        }
      }
      setScheduleDayDetailsByDate(next);
    } catch (e) {
      console.error("Schedule day-details load failed", e);
      setScheduleDayDetailsByDate({});
    }
  }, []);

  /** Loads weather for the visible range without blocking Schedule blocks or DayDetails. */
  const reloadScheduleWeatherForRange = useCallback(async (startIso, endIso) => {
    scheduleWeatherFetchGenRef.current += 1;
    const gen = scheduleWeatherFetchGenRef.current;
    // Clear stale rows so out-of-window days show loading until their segment lands.
    setScheduleWeatherByDate({});
    try {
      await loadScheduleWeatherSplitByDay(startIso, endIso, (segment) => {
        // Ignore late responses from a superseded range and merge each segment
        // (past resolves first) into the map as it arrives.
        if (gen !== scheduleWeatherFetchGenRef.current) {
          return;
        }
        setScheduleWeatherByDate((prev) => ({ ...prev, ...segment }));
      });
    } catch (e) {
      console.error("Schedule weather load failed", e);
    }
  }, []);

  useEffect(() => {
    if (!loadDayDetails && !loadWeather) {
      setScheduleDayDetailsByDate({});
      setScheduleWeatherByDate({});
      lastDayDetailsRangeKeyRef.current = null;
      return;
    }
    const vd = timelineLayout.visibleDays;
    if (!vd?.length) {
      setScheduleDayDetailsByDate({});
      setScheduleWeatherByDate({});
      lastDayDetailsRangeKeyRef.current = null;
      return;
    }
    const startIso = toLocalDateKey(vd[0]);
    const endIso = toLocalDateKey(vd[vd.length - 1]);
    const rangeKey = `${startIso}|${endIso}`;
    if (rangeKey === lastDayDetailsRangeKeyRef.current) {
      return;
    }
    lastDayDetailsRangeKeyRef.current = rangeKey;
    if (loadDayDetails) {
      void reloadScheduleDayDetailsForRange(startIso, endIso);
    } else {
      setScheduleDayDetailsByDate({});
    }
    if (loadWeather) {
      void reloadScheduleWeatherForRange(startIso, endIso);
    } else {
      setScheduleWeatherByDate({});
    }
  }, [
    timelineLayout.visibleDays,
    loadDayDetails,
    loadWeather,
    reloadScheduleDayDetailsForRange,
    reloadScheduleWeatherForRange,
  ]);

  const scheduleSyncReloadByResource = useMemo(
    () => ({
      "schedule.groups": reloadScheduleGroups,
      "schedule.blocks": reloadScheduleBlocks,
      "schedule.aspects": reloadScheduleBlocks,
      "dispatch.aspects": reloadScheduleBlocks,
      "dispatch.day_details": () => {
        const vd = timelineLayoutRef.current?.visibleDays;
        if (!vd?.length) {
          return Promise.resolve();
        }
        return reloadScheduleDayDetailsForRange(
          toLocalDateKey(vd[0]),
          toLocalDateKey(vd[vd.length - 1])
        );
      },
    }),
    [reloadScheduleBlocks, reloadScheduleGroups, reloadScheduleDayDetailsForRange]
  );

  usePlanningSyncPoller({
    currentModule: "schedule",
    clientInstanceId,
    notify: enqueueNotification,
    onLatestEvent: setPlanningLastUpdatedFromEvent,
    reloadByResource: scheduleSyncReloadByResource,
  });

  /** Dispatch DayDetails `locked` keyed by `YYYY-MM-DD` for visible timeline days (read-only). */
  const scheduleDayLocks = useMemo(() => {
    const next = {};
    for (const [dateKey, row] of Object.entries(scheduleDayDetailsByDate)) {
      next[dateKey] = Boolean(row?.locked);
    }
    return next;
  }, [scheduleDayDetailsByDate]);

  /**
   * @param {string} dateKey `YYYY-MM-DD` local calendar key.
   * @returns {Object|null}
   */
  const getScheduleDayDetails = useCallback(
    (dateKey) => scheduleDayDetailsByDate[dateKey] ?? null,
    [scheduleDayDetailsByDate]
  );

  /**
   * @param {string} dateKey `YYYY-MM-DD` local calendar key.
   * @returns {Object|null}
   */
  const getScheduleWeather = useCallback(
    (dateKey) => scheduleWeatherByDate[dateKey] ?? null,
    [scheduleWeatherByDate]
  );

  /**
   * Visible projects are derived synchronously from the loaded job list and current
   * header layout so the side rail does not flash empty after the fetch resolves.
   */
  const visibleJobs = useMemo(() => {
    if (isScheduleDataLoading) {
      return [];
    }
    const raw = allJobs;
    const vd = timelineLayout.visibleDays;
    if (!raw.length || !vd?.length) {
      return [];
    }
    const vStart = toLocalDateKey(vd[0]);
    const vEnd = toLocalDateKey(vd[vd.length - 1]);
    const mappedJobs = raw
      .filter((j) => jobOverlapsVisibleRange(j, vStart, vEnd))
      .map(mapJobNumberToProject)
      .filter(Boolean);
    const scopedJobs = hasSingleProject
      ? mappedJobs.filter((job) => String(job.id) === singleProjectId)
      : mappedJobs;
    if (scopedJobs.length || !hasSingleProject || !singleProject) {
      return scopedJobs;
    }
    const mappedSingleProject = mapJobNumberToProject(singleProject);
    return mappedSingleProject ? [mappedSingleProject] : [];
  }, [
    allJobs,
    timelineLayout.visibleDays,
    isScheduleDataLoading,
    hasSingleProject,
    singleProjectId,
    singleProject,
  ]);

  /** Auto-expand all visible projects once on initial data availability. */
  useEffect(() => {
    if (!initialProjectExpandDone.current && visibleJobs.length > 0) {
      initialProjectExpandDone.current = true;
      setExpandedProjectIds(new Set(visibleJobs.map((p) => p.id)));
    }
  }, [visibleJobs]);

  /** Auto-expand all loaded groups once so nested rows are initially visible. */
  useEffect(() => {
    if (!initialGroupExpandDone.current && groups.length > 0) {
      initialGroupExpandDone.current = true;
      setExpandedGroupIds(new Set(groups.map((g) => g.id)));
    }
  }, [groups]);

  /** Current timeline date bounds used by schedule controls and modal defaults. */
  const dateRange = useMemo(() => {
    const vd = timelineLayout.visibleDays;
    if (vd?.length) {
      return {
        start: new Date(vd[0]),
        end: new Date(vd[vd.length - 1]),
      };
    }
    const end = new Date(timelineStart);
    end.setDate(end.getDate() + 29);
    return { start: new Date(timelineStart), end };
  }, [timelineLayout.visibleDays, timelineStart]);

  /**
   * Fast lookup set for currently visible project ids.
   * Normalize to strings — API job ids may be numbers while filter/group ids are strings.
   */
  const visibleJobIds = useMemo(
    () => new Set(visibleJobs.map((j) => String(j.id))),
    [visibleJobs]
  );

  /** Groups constrained to currently visible projects. */
  const visibleGroups = useMemo(
    () => groups.filter((g) => visibleJobIds.has(String(g.projectId))),
    [groups, visibleJobIds]
  );

  /** Visible blocks are blocks that intersect at least one currently visible day. */
  const visibleBlocks = useMemo(() => {
    const visibleDayKeys = (timelineLayout.visibleDays ?? []).map(toLocalDateKey);
    if (!visibleDayKeys.length) {
      return [];
    }
    return blocks.filter((block) => {
      if (block.job?.id == null || !visibleJobIds.has(String(block.job.id))) {
        return false;
      }
      const startKey = toLocalDateKey(block.startDate);
      const endKey = toLocalDateKey(block.endDate);
      return visibleDayKeys.some(
        (dayKey) => startKey <= dayKey && dayKey <= endKey
      );
    });
  }, [blocks, timelineLayout.visibleDays, visibleJobIds]);

  /**
   * Blocks limited to the currently selected projects. Used as the base for the
   * scope/worker option cascades so those dropdowns only offer values that are
   * still visible under the active Project filter. Uses the raw selection (not
   * the validated/effective one) to avoid a circular dependency, since Project
   * options are themselves derived from the scope/worker-filtered blocks.
   */
  const visibleBlocksInSelectedProjects = useMemo(() => {
    if (!filterProjectId.length) {
      return visibleBlocks;
    }
    const projectIds = new Set(filterProjectId);
    return visibleBlocks.filter((block) =>
      projectIds.has(String(block.job?.id))
    );
  }, [visibleBlocks, filterProjectId]);

  /** Uncurated scope options from all blocks in view; validates selections and keeps selected chips visible. */
  const scheduleFilterScopeOptionsBase = useMemo(
    () => collectWorkScopeFilterOptions(visibleBlocks),
    [visibleBlocks]
  );

  /** Uncurated worker options from all blocks/aspects in view; validates selections and keeps selected chips visible. */
  const scheduleFilterWorkerOptionsBase = useMemo(
    () =>
      collectWorkerFilterOptions(
        visibleBlocks,
        workScopeAspects,
        timelineLayout.visibleDays ?? []
      ),
    [visibleBlocks, workScopeAspects, timelineLayout.visibleDays]
  );

  // Available scope options reflect the selected projects and any selected
  // workers; already-selected scopes are merged back in so curation never
  // hides active selections.
  const scheduleFilterScopeOptions = useMemo(
    () =>
      mergeSelectedFilterOptions(
        collectWorkScopeFilterOptions(
          visibleBlocksInSelectedProjects.filter((block) =>
            blockMatchesWorkerFilter(
              block,
              workScopeAspects,
              timelineLayout.visibleDays ?? [],
              filterWorkerId
            )
          )
        ),
        scheduleFilterScopeOptionsBase,
        filterWorkScopeId
      ),
    [
      visibleBlocksInSelectedProjects,
      workScopeAspects,
      timelineLayout.visibleDays,
      filterWorkerId,
      scheduleFilterScopeOptionsBase,
      filterWorkScopeId,
    ]
  );

  // Available worker options reflect the selected projects and any selected
  // work scopes; already-selected workers are merged back in.
  const scheduleFilterWorkerOptions = useMemo(
    () =>
      mergeSelectedFilterOptions(
        collectWorkerFilterOptions(
          visibleBlocksInSelectedProjects.filter((block) =>
            blockMatchesWorkScopeFilter(block, filterWorkScopeId)
          ),
          workScopeAspects,
          timelineLayout.visibleDays ?? []
        ),
        scheduleFilterWorkerOptionsBase,
        filterWorkerId
      ),
    [
      visibleBlocksInSelectedProjects,
      workScopeAspects,
      timelineLayout.visibleDays,
      filterWorkScopeId,
      scheduleFilterWorkerOptionsBase,
      filterWorkerId,
    ]
  );

  // Selections are validated against the base lists (everything in view), not
  // the curated lists, so cross-curation never silently drops an active filter.
  const effectiveFilterWorkScopeId = useMemo(
    () => getEffectiveFilterIds(filterWorkScopeId, scheduleFilterScopeOptionsBase),
    [filterWorkScopeId, scheduleFilterScopeOptionsBase]
  );

  const effectiveFilterWorkerId = useMemo(
    () => getEffectiveFilterIds(filterWorkerId, scheduleFilterWorkerOptionsBase),
    [filterWorkerId, scheduleFilterWorkerOptionsBase]
  );

  /**
   * Scope/worker filters limit which blocks are considered when deriving visible
   * projects, project options, row heights, and rendered bars.
   */
  const visibleBlocksForScopeWorkerFiltering = useMemo(
    () =>
      filterScheduleBlocksForToolbar(
        visibleBlocks,
        workScopeAspects,
        timelineLayout.visibleDays ?? [],
        effectiveFilterWorkScopeId,
        effectiveFilterWorkerId
      ),
    [
      visibleBlocks,
      workScopeAspects,
      timelineLayout.visibleDays,
      effectiveFilterWorkScopeId,
      effectiveFilterWorkerId,
    ]
  );

  /**
   * Optionally hide jobs that lack timeline substance:
   * - No schedule groups and no blocks overlapping visible days (existing behaviour).
   * - If filtering by scope or worker, no remaining matching blocks in the visible days.
   * - Only a root Dispatch bucket group auto-created from Dispatch orphans and zero blocks.
   */
  const scheduleVisibleJobsBeforeProjectFilter = useMemo(() => {
    if (!hideEmptyProjects) {
      return visibleJobs;
    }
    const projectIdsWithGroups = new Set(
      visibleGroups.map((group) => String(group.projectId))
    );
    const projectIdsWithBlocks = new Set(
      visibleBlocksForScopeWorkerFiltering
        .map((block) => (block.job?.id == null ? null : String(block.job.id)))
        .filter((id) => id != null)
    );

    return visibleJobs.filter((project) => {
      const projectId = String(project.id);
      const hasBlocks = projectIdsWithBlocks.has(projectId);

      if (hasBlocks) {
        return true;
      }

      if (!projectIdsWithGroups.has(projectId)) {
        return false;
      }

      const groupsForJob = visibleGroups.filter(
        (g) => String(g.projectId) === projectId
      );
      const dispatchOnlyEmptyPlaceholder =
        groupsForJob.length === 1 &&
        groupsForJob[0].parentGroupId == null &&
        groupsForJob[0].name.trim() === SCHEDULE_DISPATCH_GROUP_NAME;

      if (dispatchOnlyEmptyPlaceholder) {
        return false;
      }

      return true;
    });
  }, [
    hideEmptyProjects,
    visibleBlocksForScopeWorkerFiltering,
    visibleGroups,
    visibleJobs,
  ]);

  /** Uncurated project options from all jobs in view; validates selections and keeps selected chips visible. */
  const scheduleFilterProjectOptionsBase = useMemo(
    () => collectProjectFilterOptions(visibleJobs),
    [visibleJobs]
  );

  // Available project options reflect scope/worker filtering and hide-empty;
  // already-selected projects are merged back in so curation never hides them.
  const scheduleFilterProjectOptions = useMemo(
    () =>
      mergeSelectedFilterOptions(
        collectProjectFilterOptions(scheduleVisibleJobsBeforeProjectFilter),
        scheduleFilterProjectOptionsBase,
        filterProjectId
      ),
    [
      scheduleVisibleJobsBeforeProjectFilter,
      scheduleFilterProjectOptionsBase,
      filterProjectId,
    ]
  );

  const effectiveFilterProjectId = useMemo(
    () => getEffectiveFilterIds(filterProjectId, scheduleFilterProjectOptionsBase),
    [filterProjectId, scheduleFilterProjectOptionsBase]
  );

  /**
   * Project filtering is applied after the curated project list is built so the
   * project dropdown itself only shows currently visible projects.
   */
  const visibleBlocksForProjectFiltering = useMemo(() => {
    if (!effectiveFilterProjectId.length) {
      return visibleBlocksForScopeWorkerFiltering;
    }
    const selectedProjectIds = new Set(effectiveFilterProjectId);
    return visibleBlocksForScopeWorkerFiltering.filter((block) =>
      selectedProjectIds.has(String(block.job?.id))
    );
  }, [effectiveFilterProjectId, visibleBlocksForScopeWorkerFiltering]);

  const scheduleVisibleJobs = useMemo(() => {
    if (!effectiveFilterProjectId.length) {
      return scheduleVisibleJobsBeforeProjectFilter;
    }
    const selectedProjectIds = new Set(effectiveFilterProjectId);
    return scheduleVisibleJobsBeforeProjectFilter.filter((project) =>
      selectedProjectIds.has(String(project.id))
    );
  }, [effectiveFilterProjectId, scheduleVisibleJobsBeforeProjectFilter]);

  /** Jobs shown in the left rail and timeline after hide-empty and project filtering. */
  const scheduleDisplayJobs = scheduleVisibleJobs;

  /**
   * Use all visible ids for the first ready render, then commit that same expansion
   * into state. This avoids a transient blank grid between data load and expansion effects.
   */
  const effectiveExpandedProjectIds = useMemo(
    () =>
      initialProjectExpandDone.current
        ? expandedProjectIds
        : new Set(visibleJobs.map((p) => p.id)),
    [expandedProjectIds, visibleJobs]
  );

  const effectiveExpandedGroupIds = useMemo(
    () =>
      initialGroupExpandDone.current
        ? expandedGroupIds
        : new Set(groups.map((g) => g.id)),
    [expandedGroupIds, groups]
  );

  /** Flattened project/group rows before applying the Schedule text search. */
  const scheduleRowsBeforeSearch = useMemo(
    () =>
      flattenVisibleScheduleRows({
        projects: scheduleDisplayJobs,
        groups: visibleGroups,
        expandedProjectIds: effectiveExpandedProjectIds,
        expandedGroupIds: effectiveExpandedGroupIds,
      }),
    [
      scheduleDisplayJobs,
      visibleGroups,
      effectiveExpandedProjectIds,
      effectiveExpandedGroupIds,
    ]
  );

  /**
   * Search filters the already-visible row set by row/project label or matching
   * scope bars (scope name or worker filter labels). Matching rows stay visible,
   * while bar rendering below keeps only matching scopes.
   */
  const scheduleRows = useMemo(
    () =>
      filterScheduleRowsForSearch({
        rows: scheduleRowsBeforeSearch,
        projects: scheduleDisplayJobs,
        blocks: visibleBlocksForProjectFiltering,
        aspects: workScopeAspects,
        visibleDays: timelineLayout.visibleDays ?? [],
        query: projectSearchQuery,
      }),
    [
      scheduleRowsBeforeSearch,
      scheduleDisplayJobs,
      visibleBlocksForProjectFiltering,
      workScopeAspects,
      timelineLayout.visibleDays,
      projectSearchQuery,
    ]
  );

  /** Expand or collapse a project row in the left schedule panel. */
  const toggleProject = useCallback((projectId) => {
    setExpandedProjectIds((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });
  }, []);

  /** Expand or collapse a group row in the left schedule panel. */
  const toggleGroup = useCallback((groupId) => {
    setExpandedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  }, []);

  /**
   * True when every visible project and schedule group row (after hide-empty filter)
   * is expanded using the effective expansion sets used by the grid.
   */
  const scheduleTreeFullyExpanded = useMemo(() => {
    if (scheduleDisplayJobs.length === 0) {
      return false;
    }
    const projectsOk = scheduleDisplayJobs.every((p) =>
      effectiveExpandedProjectIds.has(p.id)
    );
    const groupsOk =
      visibleGroups.length === 0 ||
      visibleGroups.every((g) => effectiveExpandedGroupIds.has(g.id));
    return projectsOk && groupsOk;
  }, [
    scheduleDisplayJobs,
    visibleGroups,
    effectiveExpandedProjectIds,
    effectiveExpandedGroupIds,
  ]);

  const scheduleExpandCollapseAllDisabled = scheduleDisplayJobs.length === 0;

  /** Expand every filtered project + group row, or collapse all when already fully expanded. */
  const toggleExpandCollapseAllScheduleProjectsAndGroups = useCallback(() => {
    if (scheduleDisplayJobs.length === 0) {
      return;
    }
    if (scheduleTreeFullyExpanded) {
      setExpandedProjectIds(new Set());
      setExpandedGroupIds(new Set());
      return;
    }
    setExpandedProjectIds(new Set(scheduleDisplayJobs.map((p) => p.id)));
    setExpandedGroupIds(new Set(visibleGroups.map((g) => g.id)));
  }, [scheduleTreeFullyExpanded, scheduleDisplayJobs, visibleGroups]);

  /** Timeline paging step derived directly from numeric zoom level (1/2/3). */
  const stepDays = useMemo(() => {
    if (zoomLevel === 1) {
      return 1;
    }
    if (zoomLevel === 2) {
      return 7;
    }
    return 30;
  }, [zoomLevel]);

  const {
    scopeSettingsModalOpen,
    scopeSettingsBlock,
    resizePreview,
    openScopeSettings,
    closeScopeSettingsModal,
    handleConfirmScopeSettings,
    handleResizePreview,
    handleResizeCommit,
    handleBlockDragOver,
    handleBlockDragEnd,
    handleBlockDragCancel,
    dispatchEditedWarningModal,
    confirmDispatchEditedWarningModal,
    cancelDispatchEditedWarningModal,
    requestWorkerConflictConfirmation,
    scopeBlockJoinModal,
    confirmScopeBlockJoinModal,
    cancelScopeBlockJoinModal,
    workerDragConflictModal,
    confirmWorkerDragConflictModal,
    cancelWorkerDragConflictModal,
  } = useScheduleBlockActions({
    blocksRef,
    workScopeAspectsRef,
    allJobsRef,
    visibleJobs,
    visibleGroups,
    workScopes,
    workers,
    showWeekends,
    setBlocks,
    setWorkScopeAspects,
    setDragPreviewBlock,
    runPlanningOperation,
    reloadScheduleBlocks,
    scheduleDayLocks,
    notify: enqueueNotification,
  });

  /** Context `blocks` add `contains_locked` from `DayDetails.locked` across each block’s date span. */
  const blocksWithContainsLocked = useMemo(
    () => mergeBlocksContainsLocked(blocks, scheduleDayLocks),
    [blocks, scheduleDayLocks]
  );

  /**
   * Block list that applies only the **whole-bar drag** preview.
   *
   * Used to compute lane layout so the row's visual lane assignment can follow a bar
   * being dragged between rows/groups. Intentionally excludes the resize preview: when
   * a user grabs a resize handle, the bar must stay in its original lane for the whole
   * gesture, otherwise the bar — and its captured pointer-target handle — shifts
   * vertically mid-drag and the browser's pointer capture is lost (which corrupts the
   * resize session for any stacked bar that wasn't already in the bottom lane).
   */
  const blocksForLaneLayout = useMemo(
    () =>
      blocksWithContainsLocked.map((b) => {
        if (dragPreviewBlock && sameScheduleBlockId(dragPreviewBlock.id, b.id)) {
          return {
            ...dragPreviewBlock,
            contains_locked: blockDateSpanHasLockedDay(
              dragPreviewBlock,
              scheduleDayLocks
            ),
          };
        }
        return b;
      }),
    [blocksWithContainsLocked, dragPreviewBlock, scheduleDayLocks]
  );

  /** Fast id set for blocks that survived the toolbar scope/worker filters. */
  const scheduleToolbarFilteredBlockIds = useMemo(
    () => new Set(visibleBlocksForProjectFiltering.map((b) => String(b.id))),
    [visibleBlocksForProjectFiltering]
  );

  /** Row height/lane layout should only account for bars visible under current filters. */
  const blocksForVisibleLaneLayout = useMemo(
    () =>
      filterScheduleBlocksForSearch(
        blocksForLaneLayout.filter((b) =>
          scheduleToolbarFilteredBlockIds.has(String(b.id))
        ),
        projectSearchQuery,
        workScopeAspects,
        timelineLayout.visibleDays ?? []
      ),
    [
      blocksForLaneLayout,
      scheduleToolbarFilteredBlockIds,
      projectSearchQuery,
      workScopeAspects,
      timelineLayout.visibleDays,
    ]
  );

  /**
   * Aspect rows aligned with {@link blocksForLaneLayout}: only the drag-preview block's
   * aspects are re-projected onto the new span (so `getBlockSpan` sees the previewed
   * dates during a row/group move). Resize previews are intentionally not applied — see
   * the note on {@link blocksForLaneLayout}.
   */
  const aspectsForLaneLayout = useMemo(() => {
    if (!dragPreviewBlock) {
      return workScopeAspects;
    }
    const blockId = dragPreviewBlock.id;
    const prevForBlock = workScopeAspects.filter((d) =>
      sameScheduleBlockId(getAspectBlockId(d), blockId)
    );
    return [
      ...workScopeAspects.filter(
        (d) => !sameScheduleBlockId(getAspectBlockId(d), blockId)
      ),
      ...mergeAspectDaysAfterBlockSpanChange(dragPreviewBlock, prevForBlock, {
        pairBySortedIndexOnEqualLength: true,
      }),
    ];
  }, [workScopeAspects, dragPreviewBlock]);

  /** Display-time block list that applies in-progress resize or drag preview geometry. */
  const displayBlocks = useMemo(
    () =>
      blocksWithContainsLocked.map((b) => {
        if (dragPreviewBlock && sameScheduleBlockId(dragPreviewBlock.id, b.id)) {
          return {
            ...dragPreviewBlock,
            contains_locked: blockDateSpanHasLockedDay(
              dragPreviewBlock,
              scheduleDayLocks
            ),
          };
        }
        if (resizePreview && sameScheduleBlockId(resizePreview.blockId, b.id)) {
          const resized = {
            ...b,
            startDate: resizePreview.startDate,
            endDate: resizePreview.endDate,
          };
          return {
            ...resized,
            contains_locked: blockDateSpanHasLockedDay(resized, scheduleDayLocks),
          };
        }
        return b;
      }),
    [
      blocksWithContainsLocked,
      dragPreviewBlock,
      resizePreview,
      scheduleDayLocks,
    ]
  );

  /** {@link displayBlocks} restricted to toolbar/search-filtered blocks for timeline bars. */
  const blocksForScopeBars = useMemo(() => {
    const toolbarFilteredBlocks = displayBlocks.filter((b) =>
      scheduleToolbarFilteredBlockIds.has(String(b.id))
    );
    return filterScheduleBlocksForSearch(
      toolbarFilteredBlocks,
      projectSearchQuery,
      workScopeAspects,
      timelineLayout.visibleDays ?? []
    );
  }, [
    displayBlocks,
    scheduleToolbarFilteredBlockIds,
    projectSearchQuery,
    workScopeAspects,
    timelineLayout.visibleDays,
  ]);

  /** Display-time aspect rows that mirror block span/row previews before commit. */
  const displayWorkScopeAspects = useMemo(() => {
    const previewBlock = dragPreviewBlock
      ?? (resizePreview
        ? blocks.find((b) => sameScheduleBlockId(b.id, resizePreview.blockId))
        : null);
    const nextBlock = dragPreviewBlock
      ?? (previewBlock && resizePreview
        ? {
          ...previewBlock,
          startDate: resizePreview.startDate,
          endDate: resizePreview.endDate,
        }
        : null);
    if (!nextBlock) {
      return workScopeAspects;
    }
    const blockId = nextBlock.id;
    const prevForBlock = workScopeAspects.filter((d) =>
      sameScheduleBlockId(getAspectBlockId(d), blockId)
    );
    return [
      ...workScopeAspects.filter(
        (d) => !sameScheduleBlockId(getAspectBlockId(d), blockId)
      ),
      ...mergeAspectDaysAfterBlockSpanChange(nextBlock, prevForBlock, {
        pairBySortedIndexOnEqualLength: Boolean(dragPreviewBlock),
        preserveSkippedWeekendsByWeekday: Boolean(dragPreviewBlock) && !showWeekends,
        skipNewWeekendAspectDays: Boolean(resizePreview) && !showWeekends,
      }),
    ];
  }, [workScopeAspects, blocks, dragPreviewBlock, resizePreview, showWeekends]);

  /**
   * Frontend-only visual row/lane layout for overlapping scope bars.
   *
   * Layout reads from the drag-preview-only block/aspect snapshots so that resize
   * previews don't trigger mid-gesture lane reassignment for the bar being resized
   * (see {@link blocksForLaneLayout}). Bar horizontal geometry still reflects the
   * resize preview via `displayBlocks` / `displayWorkScopeAspects` in
   * {@link buildTimelineBars}; lanes "settle" naturally on commit.
   */
  const scheduleRowLayout = useMemo(
    () =>
      buildScheduleOverlapLayout({
        scheduleRows,
        blocks: blocksForVisibleLaneLayout,
        workScopeAspects: aspectsForLaneLayout,
        visibleDays: timelineLayout.visibleDays || [],
        baseRowHeight: SCHEDULE_ROW_HEIGHT,
      }),
    [
      blocksForVisibleLaneLayout,
      aspectsForLaneLayout,
      scheduleRows,
      timelineLayout.visibleDays,
    ]
  );

  /** Render-ready timeline bars built from blocks, aspects, rows, and viewport layout. */
  const scopeBars = useMemo(
    () =>
      buildTimelineBars({
        blocks: blocksForScopeBars,
        workScopeAspects: displayWorkScopeAspects,
        workScopes,
        groups: visibleGroups,
        scheduleRows,
        visibleDays: timelineLayout.visibleDays || [],
        cellWidth:
          timelineLayout.cellWidth || SCHEDULE_DEFAULT_CELL_WIDTH_PX,
        rowHeight: SCHEDULE_ROW_HEIGHT,
        rowTopById: scheduleRowLayout.rowTopById,
        laneByBlockId: scheduleRowLayout.laneByBlockId,
      }),
    [
      blocksForScopeBars,
      displayWorkScopeAspects,
      workScopes,
      visibleGroups,
      scheduleRows,
      timelineLayout,
      scheduleRowLayout,
    ]
  );

  /** Shift timeline start left/right by the current zoom step. */
  const moveTimeline = useCallback(
    (direction) => {
      setTimelineStart((prev) => {
        const next = new Date(prev);
        next.setDate(next.getDate() + direction * stepDays);
        return next;
      });
    },
    [stepDays]
  );

  /** Reset timeline to the start of the current week. */
  const resetTimelineToToday = useCallback(() => {
    setTimelineStart(getWeekStart(today()));
    setHorizontalScrollLeft(0);
  }, []);

  const {
    createGroupModalOpen,
    openCreateGroup,
    handleCloseCreateGroupModal,
    handleSaveScheduleGroup,
    renameScheduleGroup,
    removeScheduleGroup,
  } = useScheduleGroupActions({
    groups,
    setGroups,
    setExpandedProjectIds,
    setExpandedGroupIds,
    setBlocks,
    runPlanningOperation,
  });

  const {
    aspectWorkersModal,
    scheduleCrewClipboard,
    workerUnskipConflictModal,
    confirmWorkerUnskipConflictModal,
    cancelWorkerUnskipConflictModal,
    handleSkippedReasonChange,
    closeAspectWorkersModal,
    submitAspectWorkersModal,
    handleBarDayShortClick,
    handleOpenScopeCrewModal,
    handleRemoveDayCrewOverride,
    handleCommitOverrideStripRange,
    handleToggleAspectSkipped,
    copyScheduleCrew,
    pasteScheduleCrew,
    scheduleWorkerModalUsedWorkers,
    applyAspectWorkersToAllDays,
  } = useScheduleCrewActions({
    blocks,
    blocksRef,
    workScopeAspects,
    workScopeAspectsRef,
    workers,
    blockDraftWorkers,
    setBlocks,
    setWorkScopeAspects,
    runPlanningOperation,
    scheduleDayLocks,
    requestWorkerConflictConfirmation,
    notify: enqueueNotification,
  });

  const {
    createModalOpen,
    createAnchorLabel,
    createDefaultWorkingDaysOnly,
    handleCloseCreateModal,
    handleCellClick,
    handleCreateScope,
    copiedScopeBlock,
    clipboardRevision,
    copyScopeBlock,
    pasteScopeBlockAtCell,
  } = useScheduleCreatePasteActions({
    visibleJobs,
    workScopes,
    allJobsRef,
    workScopeAspectsRef,
    blockDraftWorkers,
    setBlocks,
    setWorkScopeAspects,
    runPlanningOperation,
    clampLocalDateToJobRange,
    scheduleDayLocks,
    requestWorkerConflictConfirmation,
    workers,
    notify: enqueueNotification,
    applyAspectWorkersToAllDays,
  });

  const {
    deleteScopeBlockModal,
    deleteScopeBlockBusy,
    requestDeleteScopeBlock,
    closeDeleteScopeBlockModal,
    confirmDeleteScopeBlock,
  } = useScheduleDeleteScopeBlock({
    setBlocks,
    setWorkScopeAspects,
    runPlanningOperation,
  });

  /**
   * @param {string} dateKey `YYYY-MM-DD` local calendar key.
   * @returns {boolean}
   */
  const isScheduleDayLocked = useCallback(
    (dateKey) => Boolean(scheduleDayLocks[dateKey]),
    [scheduleDayLocks]
  );

  /**
   * Set the Dispatch DayDetails lock from Schedule controls.
   * @param {string} dateKey `YYYY-MM-DD` local calendar key.
   * @param {boolean} locked Next DayDetails locked value.
   * @returns {Promise<void>}
   */
  const setScheduleDayLocked = useCallback(async (dateKey, locked) => {
    const [year, month, day] = String(dateKey).split("-");
    const dateMmDdYyyy = `${month}-${day}-${year}`;
    await runPlanningOperation({
      module: "schedule",
      operation: "updatePlanningDayLock",
      resources: ["dispatch.day_details"],
      apiCall: () => setPlanningDayLocked(dateMmDdYyyy, locked),
    });
    setScheduleDayDetailsByDate((prev) => ({
      ...prev,
      [dateKey]: {
        ...(prev[dateKey] ?? { date: dateKey }),
        locked,
      },
    }));
  }, [runPlanningOperation]);

  /**
   * Unlock a Dispatch DayDetails row from the Schedule header context menu.
   * @param {string} dateKey `YYYY-MM-DD` local calendar key.
   * @returns {Promise<void>}
   */
  const unlockScheduleDay = useCallback(
    (dateKey) => setScheduleDayLocked(dateKey, false),
    [setScheduleDayLocked]
  );

  /** Expose stable schedule state/actions for ScheduleContext consumers. */
  return useMemo(
    () => ({
      compact,
      readOnly,
      singleProject,
      visibleJobs,
      groups,
      planningLastUpdated,
      dateRange,
      workScopes,
      blocks: blocksWithContainsLocked,
      workScopeAspects,
      isScheduleDataLoading,
      isScheduleWindowLoading,
      isScheduleTimelineLoading:
        isScheduleDataLoading || !(timelineLayout.visibleDays?.length > 0),
      zoomLevel,
      setZoomLevel,
      showWeekends,
      setShowWeekends,
      hideEmptyProjects,
      setHideEmptyProjects,
      projectSearchQuery,
      setProjectSearchQuery,
      scheduleViewMode,
      setScheduleViewMode,
      filterProjectId: effectiveFilterProjectId,
      setFilterProjectId,
      filterWorkScopeId: effectiveFilterWorkScopeId,
      setFilterWorkScopeId,
      filterWorkerId: effectiveFilterWorkerId,
      setFilterWorkerId,
      scheduleFilterProjectOptions,
      scheduleFilterScopeOptions,
      scheduleFilterWorkerOptions,
      timelineStart,
      timelineLayout,
      setTimelineLayout,
      horizontalScrollLeft,
      setHorizontalScrollLeft,
      scheduleDayDetailsByDate,
      getScheduleDayDetails,
      scheduleWeatherByDate,
      getScheduleWeather,
      scheduleDayLocks,
      isScheduleDayLocked,
      setScheduleDayLocked,
      unlockScheduleDay,
      createModalOpen,
      createAnchorLabel,
      createDefaultWorkingDaysOnly,
      scopeSettingsModalOpen,
      scopeSettingsBlock,
      scheduleRows,
      rowHeightsById: scheduleRowLayout.rowHeightsById,
      rowTopById: scheduleRowLayout.rowTopById,
      totalGridHeight: scheduleRowLayout.totalGridHeight,
      scopeBars,
      expandedProjectIds,
      expandedGroupIds,
      toggleProject,
      toggleGroup,
      scheduleTreeFullyExpanded,
      scheduleExpandCollapseAllDisabled,
      toggleExpandCollapseAllScheduleProjectsAndGroups,
      openCreateGroup,
      renameScheduleGroup,
      removeScheduleGroup,
      createGroupModalOpen,
      handleCloseCreateGroupModal,
      handleSaveScheduleGroup,
      moveTimeline,
      resetTimelineToToday,
      handleCloseCreateModal,
      handleCellClick,
      handleCreateScope,
      copiedScopeBlock,
      clipboardRevision,
      copyScopeBlock,
      pasteScopeBlockAtCell,
      openScopeSettings,
      closeScopeSettingsModal,
      handleConfirmScopeSettings,
      handleResizePreview,
      handleResizeCommit,
      handleBlockDragOver,
      handleBlockDragEnd,
      handleBlockDragCancel,
      dispatchEditedWarningModal,
      confirmDispatchEditedWarningModal,
      cancelDispatchEditedWarningModal,
      scopeBlockJoinModal,
      confirmScopeBlockJoinModal,
      cancelScopeBlockJoinModal,
      workerDragConflictModal,
      confirmWorkerDragConflictModal,
      cancelWorkerDragConflictModal,
      workerUnskipConflictModal,
      confirmWorkerUnskipConflictModal,
      cancelWorkerUnskipConflictModal,
      handleSkippedReasonChange,
      aspectWorkersModal,
      scheduleWorkerModalUsedWorkers,
      closeAspectWorkersModal,
      submitAspectWorkersModal,
      handleBarDayShortClick,
      handleOpenScopeCrewModal,
      handleRemoveDayCrewOverride,
      handleCommitOverrideStripRange,
      handleToggleAspectSkipped,
      scheduleCrewClipboard,
      copyScheduleCrew,
      pasteScheduleCrew,
      workers,
      deleteScopeBlockModal,
      deleteScopeBlockBusy,
      requestDeleteScopeBlock,
      closeDeleteScopeBlockModal,
      confirmDeleteScopeBlock,
      blockDraftWorkers,
    }),
    [
      compact,
      readOnly,
      singleProject,
      scheduleCrewClipboard,
      visibleJobs,
      groups,
      planningLastUpdated,
      dateRange,
      workScopes,
      blocksWithContainsLocked,
      workScopeAspects,
      isScheduleDataLoading,
      isScheduleWindowLoading,
      zoomLevel,
      showWeekends,
      hideEmptyProjects,
      projectSearchQuery,
      scheduleViewMode,
      effectiveFilterProjectId,
      effectiveFilterWorkScopeId,
      effectiveFilterWorkerId,
      setFilterProjectId,
      setFilterWorkScopeId,
      setFilterWorkerId,
      scheduleFilterProjectOptions,
      scheduleFilterScopeOptions,
      scheduleFilterWorkerOptions,
      timelineStart,
      timelineLayout,
      horizontalScrollLeft,
      scheduleDayDetailsByDate,
      getScheduleDayDetails,
      scheduleWeatherByDate,
      getScheduleWeather,
      scheduleDayLocks,
      isScheduleDayLocked,
      setScheduleDayLocked,
      unlockScheduleDay,
      createModalOpen,
      createAnchorLabel,
      createDefaultWorkingDaysOnly,
      scopeSettingsModalOpen,
      scopeSettingsBlock,
      scheduleRows,
      scheduleRowLayout,
      scopeBars,
      expandedProjectIds,
      expandedGroupIds,
      toggleProject,
      toggleGroup,
      scheduleTreeFullyExpanded,
      scheduleExpandCollapseAllDisabled,
      toggleExpandCollapseAllScheduleProjectsAndGroups,
      openCreateGroup,
      renameScheduleGroup,
      removeScheduleGroup,
      createGroupModalOpen,
      handleCloseCreateGroupModal,
      handleSaveScheduleGroup,
      moveTimeline,
      resetTimelineToToday,
      handleCloseCreateModal,
      handleCellClick,
      handleCreateScope,
      copiedScopeBlock,
      clipboardRevision,
      copyScopeBlock,
      pasteScopeBlockAtCell,
      openScopeSettings,
      closeScopeSettingsModal,
      handleConfirmScopeSettings,
      handleResizePreview,
      handleResizeCommit,
      handleBlockDragOver,
      handleBlockDragEnd,
      handleBlockDragCancel,
      dispatchEditedWarningModal,
      confirmDispatchEditedWarningModal,
      cancelDispatchEditedWarningModal,
      scopeBlockJoinModal,
      confirmScopeBlockJoinModal,
      cancelScopeBlockJoinModal,
      workerDragConflictModal,
      confirmWorkerDragConflictModal,
      cancelWorkerDragConflictModal,
      workerUnskipConflictModal,
      confirmWorkerUnskipConflictModal,
      cancelWorkerUnskipConflictModal,
      handleSkippedReasonChange,
      aspectWorkersModal,
      scheduleWorkerModalUsedWorkers,
      closeAspectWorkersModal,
      submitAspectWorkersModal,
      handleBarDayShortClick,
      handleOpenScopeCrewModal,
      handleRemoveDayCrewOverride,
      handleCommitOverrideStripRange,
      handleToggleAspectSkipped,
      copyScheduleCrew,
      pasteScheduleCrew,
      workers,
      deleteScopeBlockModal,
      deleteScopeBlockBusy,
      requestDeleteScopeBlock,
      closeDeleteScopeBlockModal,
      confirmDeleteScopeBlock,
      blockDraftWorkers,
    ]
  );
}
